import React, { JSX, useEffect } from 'react';
import {
  Image,
  ImageField,
  LinkField,
  SitecoreContextValue,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { StyleguideComponentProps } from 'lib/component-props';
import { ItemValue } from 'components/types/ListItem';

type LPNavigationProps = StyleguideComponentProps & {
  fields: {
    Background: ItemValue;
    Logo1: ImageField;
    Logo2: ImageField;
    Logo3: ImageField;
    'Logo Alignment': ItemValue;
    'Link 1': LinkField;
    'Link 1 Type': ItemValue;
    'Link 2': LinkField;
    'Link 2 Type': ItemValue;
  };
};

interface CustomSitecoreContextValue {
  sitecoreContext: SitecoreContextValue;
}
const relativePathStartsWith = (path: unknown, value: string): boolean => {
  return typeof path === 'string' && path.startsWith(`${value}`);
};

const LPNavigation = (props: LPNavigationProps): JSX.Element => {
  if (!props || !props.fields) {
    return <div></div>;
  }
  const context: CustomSitecoreContextValue = useSitecoreContext({
    updatable: false,
  });

  const {
    Background,
    Logo1,
    Logo2,
    Logo3,
    'Link 1': Link1,
    'Link 1 Type': Link1Type,
    'Link 2': Link2,
    'Link 2 Type': Link2Type,
  } = props.fields;
  const LogoAlignment = props.fields['Logo Alignment'];
  const hasLink = Link1?.value?.href || Link2?.value?.href;
  const alignmentClass = hasLink
    ? 'lp-cta-nav justify-left'
    : LogoAlignment?.name?.toLowerCase() == 'left'
      ? 'justify-left'
      : 'justify-center';
  const logoNumClass =
    Logo2.value?.src && Logo3.value?.src
      ? 'all-logos'
      : Logo2.value?.src
        ? 'two-logos'
        : 'one-logo';

  const ctaClass = hasLink && 'lp-cta-nav';

  const getButtonClass = (
    linkType: ItemValue | undefined,
    isMobileFullWidth: boolean
  ) => {
    const typeName = linkType?.name?.toLowerCase() || 'primary';
    const baseClass = `cta-btn btn-${typeName} no-icon`;
    return isMobileFullWidth ? `${baseClass} btn-mobile-fw` : baseClass;
  };

  const setHeaderHeight = () => {
    const html = document.getElementsByTagName('html')[0];
    const header = document.getElementsByTagName('header')[0];
    const height = header.offsetHeight;
    document.documentElement.style.setProperty(
      '--header-height',
      `${height}px`
    );
    html.style.setProperty('scroll-padding-top', 'var(--header-height, 0px)');
  };

  const urlPath = context?.sitecoreContext
  ? context.sitecoreContext.itemPath
  : '';

  let isSistersHistory = false;

  if (
    relativePathStartsWith(urlPath, '/sisters-history')
  ) {
    isSistersHistory = true;
  }

  useEffect(() => {
    setHeaderHeight();
    const handleResize = () => {
      setHeaderHeight();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <header
      className={`overhauled lp-navigation ${ctaClass}`}
      id={`lp-navigation-${props.rendering?.uid}`}
    >
      <div
        className={`${Background?.name ? Background.name : 'bg-color-base'}`}
      >
        <div className="jss-inner-container">
          <div
            className={`navigation-container ${alignmentClass} ${logoNumClass} ${isSistersHistory ? 'sisters-navigation' : ''}`}
          >
            {props.fields.Logo1 && (
              <Image field={Logo1.value} className="logo-one" />
            )}
            {props.fields.Logo2 && (
              <Image field={Logo2.value} className="logo-two" />
            )}
            {props.fields.Logo3 && (
              <Image field={Logo3.value} className="logo-three" />
            )}
            {hasLink && (
              <div className="cta-links-wrapper">
                {Link1?.value?.href && (
                  <a
                    href={Link1.value.href}
                    className={getButtonClass(
                      Link1Type,
                      logoNumClass !== 'one-logo'
                    )}
                  >
                    {Link1.value.text}
                  </a>
                )}
                {Link2?.value?.href && (
                  <a
                    href={Link2.value.href}
                    className={getButtonClass(
                      Link2Type,
                      logoNumClass !== 'one-logo'
                    )}
                  >
                    {Link2.value.text}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default LPNavigation;
